/* eslint-disable camelcase */
import { captureException } from '@sentry/browser';
import { useQuery } from '@tanstack/react-query';
import { api } from 'controllers/network/apiClient';
import { useCallback, useEffect, useMemo } from 'react';

import {
  getMessageDetailsValidationError,
  parseMessageDetails,
} from './schema';
import { MessageDetails } from './types';
import {
  OverrideEntry,
  useMessageDetailsOverride,
} from './useMessageDetailsOverride';

export const MESSAGE_DETAILS_QUERY_KEY = 'messageDetails';

// Endpoint path is the FE's working assumption pending Chase's API stub.
// Matches the existing `/v2/sent_messages/<id>` resource pattern; the
// `/details` suffix is the new traceability sibling endpoint.
const messageDetailsUrl = (sentMessageId: number, shopId?: number): string => {
  const base = `/v2/sent_messages/${sentMessageId}/details`;
  if (typeof shopId !== 'number') return base;
  const params = new URLSearchParams();
  params.append('shop_id', shopId.toString());
  return `${base}?${params.toString()}`;
};

type AxiosLikeError = { response?: { status?: number } } | null | undefined;

const isStatus = (err: unknown, status: number): boolean => {
  const e = err as AxiosLikeError;
  return e?.response?.status === status;
};

export type MessageDetailsStatus =
  | 'loading'
  | 'live'
  | 'override'
  | 'override-forced'
  | 'empty'
  | 'error';

export interface UseMessageDetailsResult {
  status: MessageDetailsStatus;
  payload: MessageDetails | null;
  override: OverrideEntry | null;
  // True only in the 'live' status when a saved override exists but is
  // being suppressed by valid live data. Drawer surfaces a "Force override"
  // affordance in this case.
  hasSuppressedOverride: boolean;
  // Refetches the details query. Wired to the Retry affordance shown in the
  // 'error' status; harmless to call in any other status.
  retry: () => void;
}

/**
 * Resolves Message Details for a given sent_message_id by precedence:
 *
 *   1. URL-forced override (admin + saved override + ?messageDetailsForceOverride=1)
 *   2. Live API data, if 200 and zod-valid
 *   3. Saved override (admin only)
 *   4. Error (non-404 failure — network/5xx after retries)
 *   5. Empty (404 or schema-invalid 200)
 *
 * The hook auto-flips from 'override' to 'live' the moment the API starts
 * returning valid data — no consumer action required. 'empty' means the
 * snapshot genuinely doesn't exist (older messages will never get one), while
 * 'error' means we couldn't find out — consumers should offer `retry`.
 */
export const useMessageDetails = (
  sentMessageId: number | undefined,
  shopId?: number,
): UseMessageDetailsResult => {
  const { override, isForced } = useMessageDetailsOverride();

  const query = useQuery({
    queryKey: [MESSAGE_DETAILS_QUERY_KEY, sentMessageId, shopId],
    queryFn: () =>
      // Keep the axios error shape so retry/isStatus can recognise 404s.
      // Default `throwErrorAsString: true` flattens the error into a string
      // which strips `error.response.status` and breaks the retry rule.
      api.get<unknown>(messageDetailsUrl(sentMessageId as number, shopId), {
        throwErrorAsString: false,
      }),
    enabled: typeof sentMessageId === 'number',
    // Snapshot is immutable on the backend, so successful responses cache
    // forever. 404s aren't cached as data and naturally refetch on next
    // mount, which gives us the "auto-flip when API ships" behavior.
    staleTime: Infinity,
    retry: (count, err) => {
      if (isStatus(err, 404)) return false;
      return count < 2;
    },
  });

  const liveData = useMemo(
    () => (query.data !== undefined ? parseMessageDetails(query.data) : null),
    [query.data],
  );

  // A 200 payload that fails schema validation renders the same empty state
  // as a 404, so contract drift between the API and this schema is invisible
  // without a report. Grouped under one stable message; the failing field
  // path travels in `extra`.
  useEffect(() => {
    if (query.data === undefined || liveData !== null) return;
    captureException(
      new Error('[MessageDetails] live payload failed schema validation'),
      {
        extra: {
          sentMessageId,
          validationError: getMessageDetailsValidationError(query.data)
            ?.message,
        },
      },
    );
  }, [query.data, liveData, sentMessageId]);

  // Memoized so a consumer can safely put it in an effect dependency array.
  const { refetch } = query;
  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  if (isForced && override) {
    return {
      status: 'override-forced',
      payload: override.payload,
      override,
      hasSuppressedOverride: false,
      retry,
    };
  }

  if (liveData) {
    return {
      status: 'live',
      payload: liveData,
      override,
      hasSuppressedOverride: override !== null,
      retry,
    };
  }

  if (query.isLoading && !override) {
    return {
      status: 'loading',
      payload: null,
      override: null,
      hasSuppressedOverride: false,
      retry,
    };
  }

  if (override) {
    return {
      status: 'override',
      payload: override.payload,
      override,
      hasSuppressedOverride: false,
      retry,
    };
  }

  // A 404 means the snapshot doesn't exist ('empty'); any other failure means
  // we couldn't find out ('error'). Schema-invalid 200s stay 'empty' — a
  // retry would return the same drifted payload.
  if (query.isError && !isStatus(query.error, 404)) {
    return {
      status: 'error',
      payload: null,
      override: null,
      hasSuppressedOverride: false,
      retry,
    };
  }

  return {
    status: 'empty',
    payload: null,
    override: null,
    hasSuppressedOverride: false,
    retry,
  };
};
